import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const mougins: ChauffeurPage = {
  slug: "mougins",
  title: "VTC & private chauffeur in Mougins",
  subtitle:
    "Private chauffeur in Mougins: gastronomic village, golf courses, international schools and estates above Cannes.",
  keywords: [
    "vtc mougins",
    "private chauffeur mougins",
    "mougins village chauffeur",
  ],
  hero: {
    title: "VTC & private chauffeur in Mougins",
    subtitle:
      "The hilltop village of gastronomy, just above Cannes: a chauffeur for estates, golf and fine dining.",
    keywords: [
      "VTC Mougins",
      "Mougins village chauffeur",
      "Mougins golf chauffeur",
    ],
    imageSrc: heroImage.src,
    imageAlt: "VTC & private chauffeur in Mougins",
    badges: ["Above Cannes", "Golf & gastronomy", "School runs"],
  },
  benefits: {
    heading: "Why a chauffeur in Mougins",
    items: [
      {
        title: "High-end residential",
        description:
          "Estates and villas scattered across the hills: drop-off at the gate and a dedicated chauffeur for families settled in Mougins.",
      },
      {
        title: "Capital of gastronomy",
        description:
          "The legacy of Roger Vergé and Le Moulin de Mougins, Michelin tables of the Old Village: dinner there and back with no constraint.",
      },
      {
        title: "Golf & art of living",
        description:
          "Royal Mougins and nearby courses, art galleries and the MACM museum: flexible day-long travel.",
      },
      {
        title: "International schools",
        description:
          "Close to Mougins School and the international establishments: regular, punctual and secure school runs.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Mougins",
    services: [
      {
        title: "Residential hourly hire",
        text: "A dedicated chauffeur for owners and families, from the Mougins hills to Cannes, Sophia Antipolis or the airport.",
      },
      {
        title: "Gastronomy transfers",
        text: "Tables of the Old Village and the plain: there and back for lunch or dinner, with no driving constraint.",
      },
      {
        title: "Golf transfers",
        text: "Royal Mougins and the region's courses, with luggage space for the bags and timing set around tee-off.",
      },
      {
        title: "School runs",
        text: "Regular trips to Mougins School and the international schools, with punctuality and trusted chauffeurs.",
      },
      {
        title: "Airport & Cannes transfers",
        text: "Mougins ↔ Nice airport with flight tracking, and a quick link to the Croisette for Cannes events.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Old Village of Mougins",
        description: "Hilltop village, lanes, art galleries and restaurants.",
      },
      {
        name: "MACM museum",
        description:
          "Museum of Classical Art of Mougins, in the heart of the village.",
      },
      {
        name: "Royal Mougins Golf",
        description: "Course and resort, tee-off and pick-up timed to play.",
      },
      {
        name: "Mougins School",
        description: "International school, regular school runs.",
      },
      {
        name: "Residential estates",
        description:
          "Villas and properties on the hills, drop-off at the gate.",
      },
      {
        name: "Mougins-le-Haut",
        description: "Modern residential quarter above the plain.",
      },
    ],
  },
  generic: {
    heading: "Mougins, the gastronomic village above Cannes",
    subheading:
      "Residential, golf and great tables, minutes from the Croisette and Sophia Antipolis.",
    paragraphs: [
      "Mougins built its reputation on gastronomy: the legacy of Roger Vergé and Le Moulin de Mougins, the Michelin tables and the art galleries of the Old Village make it a landmark of Riviera living. A chauffeur handles the journey to and from dinner, with no constraint of driving after the meal.",
      "The village is also a sought-after residential area, made of estates and villas scattered across the hills, with its golf courses and international schools. For families who live here, a dedicated chauffeur simplifies daily life: school runs, golf and appointments, with no parking or coordination worries.",
      "Minutes from Cannes and Sophia Antipolis, Mougins also serves as overflow lodging during the major Cannes events, when the Croisette is fully booked. The chauffeur then links villa and festival hall while avoiding the bottlenecks.",
    ],
    bullets: ["Drop-off at the gate", "Dinner return", "Regular school runs"],
  },
  process: {
    heading: "Book a chauffeur in Mougins",
    steps: [
      "Share your needs (villa, golf, school, restaurant, airport, Cannes).",
      "We set the timing around your tee-off, school runs or reservations.",
      "The chauffeur appears at the villa gate or the address given.",
      "Direct transfer or hourly hire for the day depending on your programme.",
    ],
  },
  faq: [
    {
      q: "Do you offer a dedicated chauffeur for Mougins families?",
      a: "Yes. For owners and families settled on the Mougins hills, we offer hourly hire with a dedicated chauffeur: school runs, golf, appointments and dinners, with drop-off at the gate.",
    },
    {
      q: "Do you handle school runs to Mougins School?",
      a: "Yes, we organise regular school runs to Mougins School and the international schools of the area, with trusted chauffeurs and punctuality suited to class hours.",
    },
    {
      q: "How long is it from Mougins to the Croisette in Cannes?",
      a: "Around fifteen minutes outside peak hours. During the Festival and the major trade shows, the chauffeur anticipates the bottlenecks to link your villa to the festival hall on time.",
    },
    {
      q: "Do you handle golf transfers with the bags?",
      a: "Yes, to Royal Mougins and the nearby courses, with a vehicle offering the luggage space needed for the bags and timing set around your tee-off.",
    },
  ],
  relatedLinks: [
    {
      label: "Cannes to Nice Airport with a chauffeur",
      href: "/en/chauffeur/transfers/cannes-nice-airport",
    },
    {
      label: "Cannes to Monaco with a chauffeur",
      href: "/en/chauffeur/transfers/cannes-monaco",
    },
    { label: "Transfers and routes", href: "/en/chauffeur/transfers" },
  ],
  cta: {
    title: "Your private chauffeur in Mougins",
    text: "Villa, golf, school or Michelin table: book your dedicated chauffeur in Mougins.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Car service Mougins — private chauffeur, golf & schools",
    description:
      "Private car service in Mougins: gastronomic village, golf, international schools and estates above Cannes. School runs, airport, 24/7. Book now.",
    canonical: "/en/chauffeur/mougins",
  },
};

export default mougins;
